import {
  LayoutDashboard,
  ShoppingCart,
  ClipboardList,
  FolderKanban,
  Gavel,
  Package,
  Truck,
  Building2,
  BarChart3,
  FileCode2,
  Shield,
  ShieldCheck,
  Scale,
  MapPin,
  AlertTriangle,
  Users,
  Layers,
  Compass,
  FileText,
  FolderCheck,
  HardHat,
  CalendarRange,
  TrendingUp,
  Calculator,
  NotebookPen,
  Boxes,
  FileSignature,
  ClipboardCheck
} from 'lucide-react';

// Roles con acceso al módulo Construcción (el detalle por acción está en
// src/construction/permissions.js).
export const CONSTRUCTION_ROLES = [
  'super_user',
  'project_director',
  'area_manager',
  'project_manager',
  'field_technician',
  'contract_manager',
  'purchasing_manager',
  'purchase_controller',
  'abogado',
  'office_staff'
];

// Fuente única de la navegación: la usan Sidebar (render del menú) y Layout
// (breadcrumbs y título de página), para no mantener dos listas separadas.
export const NAV_ITEMS = [
  {
    id: 'home-module',
    label: 'Inicio',
    icon: LayoutDashboard,
    children: [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }]
  },
  {
    id: 'projects-module',
    label: 'Proyectos',
    icon: FolderKanban,
    roles: ['project_manager', 'purchasing_manager', 'super_user'],
    pageId: 'projects',
    children: [
      { id: 'projects', label: 'Buscar proyecto', icon: FolderKanban }
    ]
  },
  {
    id: 'development-module',
    label: 'Desarrollo',
    icon: Compass,
    roles: ['project_manager', 'contract_manager', 'super_user'],
    children: [
      { id: 'development-portfolio', label: 'Portafolio', icon: MapPin },
      { id: 'development-permits', label: 'Permisos & PAS', icon: FileText },
      { id: 'development-assistant', label: 'Asistente PAS', icon: FolderCheck },
      { id: 'development-alerts', label: 'Alertas', icon: AlertTriangle },
      { id: 'development-reporting', label: 'Reportes', icon: BarChart3 },
      { id: 'development-team', label: 'Equipo', icon: Users }
    ]
  },
  {
    id: 'suppliers-module',
    label: 'Proveedores & Compliance',
    icon: Building2,
    roles: ['comprador', 'purchasing_manager', 'contract_manager', 'abogado', 'purchase_controller', 'super_user'],
    children: [
      { id: 'suppliers-overview', label: 'Panel general', icon: Building2 },
      { id: 'suppliers-legal', label: 'Info legal y admin', icon: Scale },
      { id: 'suppliers-compliance', label: 'Matriz cumplimiento', icon: ShieldCheck },
      { id: 'suppliers-history', label: 'Historial de trabajos', icon: ClipboardList },
      { id: 'suppliers-orders', label: 'Órdenes y facturación', icon: ShoppingCart }
    ]
  },
  {
    id: 'procurement-module',
    label: 'Abastecimiento',
    icon: Layers,
    roles: ['comprador', 'purchasing_manager', 'purchase_controller', 'super_user', 'project_manager'],
    children: [
      { id: 'procurement-requisitions', label: 'Solicitud de pedido (PR)', icon: ClipboardList },
      { id: 'procurement-tenders', label: 'Licitaciones', icon: Gavel },
      { id: 'procurement-orders', label: 'Órdenes de compra', icon: ShoppingCart },
      { id: 'procurement-approvals', label: 'Flujo aprobación', icon: ShieldCheck },
      { id: 'procurement-inventory', label: 'Inventario', icon: Package },
      { id: 'procurement-shipments', label: 'Envíos a terreno', icon: Truck },
      { id: 'procurement-suppliers', label: 'Compliance proveedores', icon: Building2 }
    ]
  },
  {
    id: 'construction-module',
    label: 'Construcción',
    icon: HardHat,
    roles: CONSTRUCTION_ROLES,
    children: [
      { id: 'construction-schedule', label: 'Cronograma de obra', icon: CalendarRange },
      { id: 'construction-progress', label: 'Avance físico', icon: TrendingUp },
      { id: 'construction-costs', label: 'Control de costos (EVM)', icon: Calculator },
      { id: 'construction-daily', label: 'Parte diario', icon: NotebookPen },
      { id: 'construction-materials', label: 'Consumo de materiales', icon: Boxes },
      { id: 'construction-contracts', label: 'Contratistas y EP', icon: FileSignature },
      { id: 'construction-quality', label: 'HSE / Calidad', icon: ClipboardCheck }
    ]
  },
  { id: 'reports', label: 'Reportes', icon: BarChart3 },
  { id: 'api', label: 'API & Integraciones', icon: FileCode2 },
  { id: 'admin', label: 'Administración', icon: Shield, roles: ['purchasing_manager', 'purchase_controller', 'super_user'] }
];

// Páginas sin entrada propia en el menú (accesibles por navegación directa),
// usadas solo para resolver el breadcrumb/título.
const EXTRA_PAGE_LABELS = {
  'projects-budget': 'Presupuesto',
  'projects-timeline': 'Cronograma',
  'projects-risks': 'Avance y Riesgos',
  'projects-milestones': 'Registro de Hitos',
  'projects-status': 'Status General Proyectos',
  'development-geo': 'Referencia Geográfica',
  profile: 'Perfil de Usuario'
};

const flatIndex = (() => {
  const index = new Map();
  NAV_ITEMS.forEach((item) => {
    if (item.children) {
      item.children.forEach((child) => {
        index.set(child.id, { moduleLabel: item.label, moduleId: item.id, pageLabel: child.label });
      });
      if (item.pageId) {
        index.set(item.pageId, { moduleLabel: item.label, moduleId: item.id, pageLabel: item.label });
      }
    } else {
      index.set(item.id, { moduleLabel: null, moduleId: item.id, pageLabel: item.label });
    }
  });
  Object.entries(EXTRA_PAGE_LABELS).forEach(([pageId, pageLabel]) => {
    if (!index.has(pageId)) {
      const prefix = pageId.split('-')[0];
      const parent = NAV_ITEMS.find((item) => item.id === `${prefix}-module`);
      index.set(pageId, { moduleLabel: parent?.label || null, moduleId: parent?.id || null, pageLabel });
    }
  });
  return index;
})();

export const getPageMeta = (pageId) =>
  flatIndex.get(pageId) || { moduleLabel: null, moduleId: null, pageLabel: 'Panel Principal' };
